// ── 네이버 플레이스 텍스트 파싱 ──────────────────────────────────────────────
// utils.js 300줄 초과로 분리 (2026-04-15)
//
// 네이버 플레이스에서 복사한 텍스트에서 상호·업종·주소·전화번호 추출
// 첫 줄 구조: [상호][지점명?][업종] — 공백·콤마 유무 관계없이 모두 처리
//
// 예시:
//   '광주 렛츠롤 주짓수&킥복싱체육관'     → 상호:'광주 렛츠롤 주짓수&킥복싱'  업종:'체육관'
//   '광주킥복싱 금호디짐킥복싱,무에타이'  → 상호:'광주킥복싱 금호디짐'         업종:'킥복싱,무에타이'
//   '광주 떡케이크 구하당떡,한과'          → 상호:'광주 떡케이크 구하당'         업종:'떡,한과'

import {
  REGION_RE, PHONE_RE, INDUSTRY_SKIP_WORDS,
} from './constants';

// ── 업종 키워드 목록 (길이 내림차순 — 긴 것 먼저 매칭) ────────────────────────
const INDUSTRY_KEYWORDS = [
  // 복합 키워드 (긴 것 우선)
  '일본식라면', '오리요리', '오리구이', '오리탕', '오리백숙',
  '고기요리', '고기구이', '초밥뷔페', '순대국밥', '해물탕게장',
  '닭갈비', '요리주점', '돈까스', '돈가스', '보쌈족발', '족발보쌈',
  '닭요리', '닭발', '닭볶음탕', '닭볶음', '찜닭', '닭집',
  '장어구이', '곱창전골', '아구찜', '낙지볶음',
  '돼지고기구이', '소고기구이', '양고기구이', '숯불구이', '직화구이', '육류',
  '삼겹살', '갈비탕', '설렁탕', '해장국', '순대국', '부대찌개',
  '감자탕', '갈비찜',
  // 단일 키워드
  '한식당', '중식당', '양식당', '분식집', '고깃집',
  '한식', '중식', '양식', '일식', '일식당', '일식집',
  '초밥', '스시', '사시미', '라멘', '라면', '우동', '냉면', '쌀국수',
  '아시아음식', '동남아음식', '베트남음식',
  '치킨', '통닭', '족발', '보쌈', '갈비', '삼겹', '곱창', '막창', '대창',
  '피자', '파스타', '스테이크', '버거', '햄버거', '샌드위치',
  '떡볶이', '김밥', '분식', '덮밥', '비빔밥', '국밥', '백반', '정식',
  '카페', '베이커리', '빵집', '제과', '디저트', '케이크', '빙수',
  '포장마차', '주점', '포차', '호프', '이자카야',
  '양꼬치', '수산', '횟집', '백숙', '만두',
  '모텔', '호텔', '펜션', '리조트', '게스트하우스', '민박',
  '실내골프연습장', '스크린골프장', '퍼블릭골프장', '골프연습장', '스크린골프', '골프장', '골프',
  '두피관리', '탈모관리', '피부관리', '네일아트', '속눈썹', '왁싱',
  '두피', '헤어샵', '미용실', '헬스장', '필라테스', '요가', '헬스',
  '노래방', '노래연습장', '학원',
  // 통신/방송
  '통신',
  // 세차/디테일링
  '스팀세차', '세차', '디테일링',
  // 떡·한과
  '떡케이크', '떡집', '떡', '한과',
  // 스포츠·무도
  '체육관', '킥복싱', '주짓수', '무에타이', '복싱', '권투장',
  '태권도', '유도', '검도', '합기도', '격투기',
  // 부동산
  '중개업',
].sort((a, b) => b.length - a.length); // 긴 키워드 우선

// ── 헬퍼: 업종 키워드로 끝나는지 확인 → 매칭 키워드 반환 ────────────────────
function findIndustrySuffix(str) {
  for (const keyword of INDUSTRY_KEYWORDS) {
    if (str.endsWith(keyword)) return keyword;
  }
  return null;
}

// ── 헬퍼: 지점명(직영점·본점·지점·점) 기준으로 상호끝/업종시작 분리 ──────────
// 예) '원주점일식당' → { companyTail:'원주점', industryHead:'일식당' }
function splitByBranch(token) {
  const t = String(token || '').trim();
  const branchMarkers = ['직영점', '본점', '지점', '점'];
  for (const marker of branchMarkers) {
    const idx = t.lastIndexOf(marker);
    if (idx < 0) continue;
    const end = idx + marker.length;
    if (end >= t.length) continue; // 마커가 맨 끝이면 업종 없음
    const companyTail  = t.slice(0, end).trim();
    const industryHead = t.slice(end).trim();
    if (companyTail && industryHead) return { companyTail, industryHead };
  }
  return null;
}

// ── 헬퍼: 토큰 하나에서 상호끝·업종 분리 시도 ──────────────────────────────
// 우선순위: ① 지점명 마커 ② 업종 키워드 suffix
// 예) '주짓수&킥복싱체육관' → { companyTail:'주짓수&킥복싱', industryHead:'체육관' }
function extractFromToken(token) {
  // ① 지점명 기준 분리
  const byBranch = splitByBranch(token);
  if (byBranch) return byBranch;

  // ② 업종 키워드 suffix 매칭
  const suffix = findIndustrySuffix(token);
  if (suffix && token.length > suffix.length) {
    return { companyTail: token.slice(0, -suffix.length).trim(), industryHead: suffix };
  }
  return null;
}

// ── 주소 끝 상호명 제거 ──────────────────────────────────────────────────────
// 네이버 복사 순서가 바뀌면서 주소 줄 끝에 상호명이 붙어오는 경우가 있다.
//   예) '광주 북구 첨단연신로107번길 6 이온통신' → '광주 북구 첨단연신로107번길 6'
// 이 잔재가 남으면 지오코딩이 엉뚱한 좌표를 잡아 미팅지도 마커가 틀어진다.
// 보수적으로: ① 상호명 그대로 끝남 ② 상호명+지점마커 ③ 상호 첫 토큰이
//   숫자·도로키워드 없는 꼬리로 등장 — 인 경우에만 제거(정상 주소 훼손 방지).
function stripTrailingCompanyFromAddress(address, companyName) {
  let addr = String(address || '').trim();
  const name = String(companyName || '').trim();
  if (!addr || !name) return addr;

  if (addr.endsWith(name)) return addr.slice(0, addr.length - name.length).trim();

  for (const marker of ['직영점', '지점', '점']) {
    if (addr.endsWith(`${name}${marker}`)) {
      return addr.slice(0, addr.length - name.length - marker.length).trim();
    }
  }

  const firstToken = name.split(/\s+/)[0];
  if (firstToken && firstToken.length >= 2) {
    const idx = addr.lastIndexOf(firstToken);
    if (idx > 0) {
      const tail = addr.slice(idx);
      // 꼬리에 숫자·도로/행정구역 키워드가 없으면 = 주소가 아니라 상호 잔재로 판단
      if (!/\d/.test(tail) && !/(로|길|동|읍|면|리|번지|층|호)/.test(tail)) {
        return addr.slice(0, idx).trim();
      }
    }
  }
  return addr;
}

// ── 메인: 네이버 플레이스 텍스트 파싱 ──────────────────────────────────────
export function parseNaverPlace(text) {
  const out = { company_name: '', industry: '', address: '', phone: '' };
  const lines = String(text || '').split('\n').map((v) => v.trim()).filter(Boolean);
  if (!lines.length) return out;

  const first = lines[0].replace(/\s+/g, ' ').trim();
  out.company_name = first; // 기본값 — 아래에서 덮어씀

  // ── 첫 줄에서 상호·업종 분리 ──────────────────────────────────────────────
  // 구조: [상호][지점명?][업종1][,업종2...] — 공백 포함 여부 무관
  //
  // 전략:
  //   공백 있는 경우 → 마지막 토큰에 대해 extractFromToken 시도
  //   공백 없는 경우 → 전체 문자열에 대해 extractFromToken 시도
  //   콤마 있는 경우 → 첫 번째 콤마 기준으로 left/right 분리 후 left에 위 전략 적용

  const commaIdx = first.search(/[,/]/);
  const leftPart  = commaIdx >= 0 ? first.slice(0, commaIdx).trim() : first;
  const rightPart = commaIdx >= 0 ? first.slice(commaIdx + 1).trim() : '';

  const leftTokens = leftPart.split(/\s+/).filter(Boolean);

  let companyName = '';
  let industryHead = '';

  if (leftTokens.length >= 2) {
    // 공백 포함: 마지막 토큰만 분리 시도
    const rawCompany = leftTokens.slice(0, -1).join(' ');
    const lastToken  = leftTokens[leftTokens.length - 1];
    const result     = extractFromToken(lastToken);
    if (result) {
      companyName  = result.companyTail ? `${rawCompany} ${result.companyTail}`.trim() : rawCompany;
      industryHead = result.industryHead;
    } else {
      // 분리 불가: 전체가 상호명
      companyName = leftPart;
    }
  } else {
    // 공백 없는 단일 토큰
    const result = extractFromToken(leftPart);
    if (result) {
      companyName  = result.companyTail;
      industryHead = result.industryHead;
    } else {
      companyName = leftPart;
    }
  }

  // 상호가 빈 문자열이 되면 원본 유지 (잘못된 분리 방지)
  if (companyName) out.company_name = companyName;

  // 업종 조합: 좌측에서 추출한 업종 + 콤마 오른쪽
  if (industryHead && rightPart) {
    out.industry = `${industryHead},${rightPart}`.replace(/\s+/g, ' ').trim();
  } else if (industryHead) {
    out.industry = industryHead;
  } else if (rightPart) {
    out.industry = rightPart;
  }

  // ── 업종: 네이버 새 형식 둘째 줄 "업종[별점N][리뷰N][접근성]" ───────────────
  // 첫 줄에서 업종을 못 뽑은 경우(새 형식: 첫 줄=상호만), 둘째 줄의 '별점/리뷰' 앞부분이 업종.
  //   예) '베이커리리뷰 589' → '베이커리'
  //       '새로오픈카페,디저트리뷰 5휠체어 출입 가능' → '카페,디저트'
  //       '베이커리별점4.62리뷰 1.1만' → '베이커리'
  //       '육류,고기요리리뷰 4,393' → '육류,고기요리'
  if (!out.industry && lines.length >= 2 && /(별점|리뷰|평점)/.test(lines[1])) {
    let cat = lines[1].split(/별점|리뷰|평점/)[0].trim();
    cat = cat.replace(/^(새로\s*오픈|신규오픈|신규|NEW)\s*/i, '').trim();
    if (cat && cat.length <= 30
        && /^[가-힣A-Za-z·,/&\s]+$/.test(cat)
        && !INDUSTRY_SKIP_WORDS.some((w) => cat.includes(w))) {
      out.industry = cat;
    }
  }

  // ── 업종 fallback: 두 번째 줄 (UI 노이즈 제거 후) ────────────────────────
  if (!out.industry) {
    const noiseWords = [
      ...INDUSTRY_SKIP_WORDS, '주소', '영업시간', '전화번호', '전체보기', '배달',
      // 네이버 새 UI 탭/라벨 노이즈
      '내부', '외부', '음료', '메뉴판', '더보기', '펼쳐보기', '찾아가는길',
      '내용', '안내', '쿠폰', '동영상', '배송',
    ];
    for (let i = 1; i < lines.length; i += 1) {
      const line = lines[i];
      if (noiseWords.some((w) => line.includes(w))) continue;
      if (line.length > 40) continue;
      if (!/^[가-힣A-Za-z,\s/]+$/.test(line)) continue;
      out.industry = line;
      break;
    }
  }

  // ── 주소: '주소' 라벨 다음 줄 우선 (네이버 새 형식), 없으면 지역명 시작 줄 ──────
  let addressLine = null;
  const addrLabelIdx = lines.findIndex((l) => l === '주소');
  if (addrLabelIdx >= 0 && lines[addrLabelIdx + 1]) {
    addressLine = lines[addrLabelIdx + 1];
  } else {
    addressLine = lines.slice(1).find((v) => REGION_RE.test(v));
  }
  if (addressLine) out.address = stripTrailingCompanyFromAddress(addressLine, out.company_name);

  // ── 전화번호: 전화번호 패턴이 있는 줄 ───────────────────────────────────
  const phoneLine = lines.find((v) => PHONE_RE.test(v));
  if (phoneLine) {
    const m = phoneLine.match(PHONE_RE);
    if (m) out.phone = m[1];
  }

  return out;
}
